import { useState } from 'react';
import { minTsaiForSeats, type GameType, type RoomConfig, type RoomMode, type SeatCount } from '@big2/shared';
import { useGameState } from '../net/GameProvider.js';
import { useI18n, copyTextToClipboard } from '../i18n.js';
import { Icon } from './Icon.js';
import { VoiceChatBar } from './VoiceChatBar.js';
import { trackEvent } from '../analytics.js';
import { LobbyRoomHeader } from './lobby/LobbyRoomHeader.js';
import { LobbySeats } from './lobby/LobbySeats.js';
import { LobbySettingsPanel } from './lobby/LobbySettingsPanel.js';
import { LobbyActionsFooter } from './lobby/LobbyActionsFooter.js';
import { LobbyMatchmakingBar } from './landing/MatchmakingBanner.js';
import './lobby/lobby.css';

interface LobbyScreenProps {
  onOpenCardBacks?: () => void;
  onOpenProfile?: () => void;
  onOpenHistory?: () => void;
  onLeaveRoom?: () => void;
}

export function LobbyScreen({ onOpenCardBacks, onOpenProfile, onOpenHistory, onLeaveRoom }: LobbyScreenProps) {
  const { state, actions } = useGameState();
  const { t } = useI18n();
  const { lobby, game, playerId, reactions } = state;
  const [copiedCode, setCopiedCode] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);

  const handleLeaveRoom = () => {
    actions.leaveRoom();
    onLeaveRoom?.();
  };

  if (!lobby) return null;

  const isHost = lobby.hostId === playerId;
  const isPaid = state.profile?.tier === 'paid';
  const isFinished = lobby.phase === 'finished';
  const roomMode: RoomMode = lobby.config.mode ?? 'free';
  const gameType: GameType = lobby.config.gameType ?? 'big2';
  const isMuushig = gameType === 'muushig';
  const isTsaiRoom = roomMode === 'tsai';
  const minTsai = lobby.minTsai ?? minTsaiForSeats(lobby.config.seats, gameType);
  const winnerName = isFinished && game?.winnerId
    ? lobby.players.find((player) => player.playerId === game.winnerId)?.name ?? 'Unknown'
    : null;

  const pointLimit = lobby.config.pointLimit;
  const matchOver = Boolean(
    state.lastGameOver?.matchOver ||
    (gameType === 'big2' && pointLimit && lobby.players.some((p) => {
      const total = lobby.totals?.[p.playerId];
      return typeof total === 'number' && total >= pointLimit;
    }))
  );
  const hasExistingScores = lobby.players.some((p) => {
    const total = lobby.totals?.[p.playerId];
    return typeof total === 'number' && total > 0;
  });

  const humanPlayersCount = lobby.players.filter((player) => !player.isBot).length;
  const canStart = Boolean(lobby.config.fillWithBots || humanPlayersCount >= lobby.config.seats);
  const hasHistory = Boolean(
    onOpenHistory && state.roomHistoryLinks.some((link) => link.roomId === lobby.roomId)
  );

  const updateConfig = (updates: Partial<RoomConfig>) => {
    if (isHost && lobby.config) {
      if (updates.mode === 'tsai' && !isPaid && onOpenProfile) {
        onOpenProfile();
        return;
      }
      const nextGameType: GameType = updates.gameType ?? lobby.config.gameType ?? 'big2';
      const nextMode = updates.mode ?? lobby.config.mode ?? 'free';
      const nextFill = nextMode === 'tsai' ? false : updates.fillWithBots ?? lobby.config.fillWithBots;
      const rawSeats =
        updates.seats ??
        (nextGameType === 'muushig' && lobby.config.gameType !== 'muushig'
          ? 5
          : nextGameType === 'big2' && lobby.config.gameType !== 'big2'
          ? 4
          : lobby.config.seats);
      const nextSeats: SeatCount =
        nextGameType === 'daaluu'
          ? 5
          : (nextGameType === 'big2' || nextGameType === 'twentyone') && rawSeats === 5
          ? 4
          : rawSeats;

      actions.configureRoom({
        ...lobby.config,
        ...updates,
        seats: nextSeats,
        gameType: nextGameType,
        mode: nextGameType === 'daaluu' ? 'free' : nextMode,
        fillWithBots: nextFill,
      });
    }
  };

  const handleCopyCode = async () => {
    if (await copyTextToClipboard(lobby.roomId)) {
      trackEvent('room_code_copied', { source: 'lobby_code', room_id: lobby.roomId });
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    }
  };

  const handleCopyLink = async () => {
    if (await copyTextToClipboard(`${window.location.origin}/?room=${encodeURIComponent(lobby.roomId)}`)) {
      trackEvent('room_link_copied', { source: 'lobby_link', room_id: lobby.roomId });
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    }
  };

  return (
    <div className="lobby-screen">
      <div className="lobby-content-modern">
        <LobbyRoomHeader
          roomId={lobby.roomId}
          roomName={lobby.roomName}
          gameType={gameType}
          mode={roomMode}
          isPublic={Boolean(lobby.config.isPublic)}
          isHost={isHost}
          isPaid={isPaid}
          voiceChatEnabled={Boolean(lobby.config.voiceChatEnabled)}
          onCopyCode={handleCopyCode}
          copiedCode={copiedCode}
          onCopyLink={handleCopyLink}
          copiedLink={copiedLink}
        />

        {lobby.config.isPublic && lobby.phase === 'lobby' && (
          <LobbyMatchmakingBar
            humanCount={humanPlayersCount}
            totalSeats={lobby.config.seats}
            fillWithBots={Boolean(lobby.config.fillWithBots)}
            isHost={isHost}
            canStart={canStart}
            onStartNow={() => actions.startGame({ restartMatch: false })}
          />
        )}

        {isFinished && winnerName && (
          <p className="lobby-winner">
            <Icon name="trophy" size={20} />
            <span>{t.wonRound(winnerName)}</span>
          </p>
        )}

        <VoiceChatBar />

        <div className="lobby-main-column">
          <LobbySeats
            players={lobby.players}
            seats={lobby.config.seats}
            fillWithBots={Boolean(lobby.config.fillWithBots)}
            botDifficulty={lobby.config.botDifficulty}
            myPlayerId={playerId}
            stats={{
              isTsaiRoom,
              minTsai,
              pointLimit: lobby.config.pointLimit,
              totals: lobby.totals,
              isMuushig,
              isFinished,
              lastTsaiTransfers: lobby.lastTsaiTransfers,
              penalties: isFinished ? game?.penalties : undefined,
              scoreDeltas: isFinished ? game?.muushig?.scoreDeltas : undefined,
            }}
            reactions={reactions}
            friendsData={state.friendsData}
            myUserId={state.profile?.id}
            onSendFriendRequest={actions.sendFriendRequest}
            onOpenProfile={onOpenProfile}
            onInviteClick={handleCopyLink}
            onSendReaction={actions.sendReaction}
            onKickPlayer={isHost ? actions.kickPlayer : undefined}
            isHost={isHost}
            onChooseSeat={actions.chooseSeat}
          />

          <LobbySettingsPanel
            config={lobby.config}
            isHost={isHost}
            isPaid={isPaid}
            minTsai={minTsai}
            supportedGames={state.supportedGames}
            onUpdateConfig={updateConfig}
            onOpenProfile={onOpenProfile}
          />
        </div>

        <LobbyActionsFooter
          isHost={isHost}
          isFinished={isFinished}
          canStart={canStart}
          gameType={gameType}
          hasHistory={hasHistory}
          hasExistingScores={hasExistingScores}
          matchOver={matchOver}
          onStartGame={() => actions.startGame({ restartMatch: false })}
          onRestartMatch={() => actions.startGame({ restartMatch: true })}
          onLeaveRoom={handleLeaveRoom}
          onOpenHistory={onOpenHistory}
          onOpenCardBacks={onOpenCardBacks}
        />
      </div>
    </div>
  );
}
